import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Calendar, Gauge, Fuel, Settings } from "lucide-react";
import { ListingOverview } from "@/types/listing";
import { useListing } from "@/hooks/useListing";
import { SaleTypeBadge } from "@/components/ui/SaleTypeBadge";
import { formatPriceWithReduction } from "@/utils/priceDisplay";
import { formatMileage } from "@/utils/listingUtils";
import { fuelTypeLabels, transmissionTypeLabels } from "@/i18n/labels/vehicleLabels";
import { indexLabels } from "@/i18n";
import { useLanguage } from "@/context/LanguageContext";
import { VehicleFuelType, TransmissionType } from "@/types/enums";
import { useNativePlatform } from "@/hooks/useNativePlatform";

import { ListingCardThumbnail } from "@/components/auctions/ListingCardThumbnail";
import { NativeListingsSkeleton } from "@/components/native/NativeListingsSkeleton";
import { listingCardTransitionName } from "@/lib/native/listingNavigation";

const NATIVE_VEHICLE_GRID_SEED_KEY = "bilenia_native_vehicle_grid_seed";

function hashToUnitInterval(input: string): number {
  // Simple deterministic hash -> [0, 1). Not crypto, just stable ordering.
  let h = 2166136261;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  // Convert to unsigned 32-bit and map to [0, 1)
  return (h >>> 0) / 2 ** 32;
}

function getOrCreateNativeGridSeed(): string {
  try {
    const existing = sessionStorage.getItem(NATIVE_VEHICLE_GRID_SEED_KEY);
    if (existing) return existing;
    const seed = `${Date.now()}-${Math.random()}`;
    sessionStorage.setItem(NATIVE_VEHICLE_GRID_SEED_KEY, seed);
    return seed;
  } catch {
    // Fallback: stable enough for this render; may change on remount if storage is blocked.
    return "fallback-seed";
  }
}

interface VehicleListingsGridProps {
  onCarClick: (listing: ListingOverview, e?: React.MouseEvent) => void;
  embedMode?: boolean; // Hide title/description for embed mode
}

const VehicleListingsGrid = ({ onCarClick, embedMode = false }: VehicleListingsGridProps) => {
  const { language } = useLanguage();
  const labels = indexLabels(language);
  const { isNative } = useNativePlatform();
  const nativeSeed = useMemo(() => (isNative ? getOrCreateNativeGridSeed() : ""), [isNative]);
  // Fetch public listings for carousel (popular/active listings)
  const { getPublicListingsCarousel } = useListing();
  const publicListingsQuery = getPublicListingsCarousel(100); // Fetch up to 100 for 20 rows × 5

  const listings = useMemo(() => {
    if (!publicListingsQuery?.data) return [];
    // Filter to only show ACTIVE listings, then shuffle and take up to 100 (20 rows × 5)
    const activeListings = publicListingsQuery.data.filter(
      (listing) => listing.status === "active"
    );
    if (isNative) {
      // In native, keep a stable “random” order so back-navigation doesn't scramble the grid.
      const keyed = activeListings.map((l) => ({
        listing: l,
        key: hashToUnitInterval(`${nativeSeed}:${l.id}`),
      }));
      keyed.sort((a, b) => a.key - b.key);
      return keyed.map((k) => k.listing).slice(0, 100);
    }
    const shuffled = [...activeListings].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, 100);
  }, [publicListingsQuery?.data, isNative, nativeSeed]);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat("sv-SE", {
      style: "currency",
      currency: "SEK",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  const formatFuelType = (fuelType?: string) => {
    if (!fuelType) return "-";
    const fuelLabels = fuelTypeLabels(language);
    const key = fuelType.toUpperCase() as VehicleFuelType;
    return fuelLabels[key] || fuelType;
  };

  const formatTransmission = (transmission?: string) => {
    if (!transmission) return "-";
    const transLabels = transmissionTypeLabels(language);
    const key = transmission.toUpperCase() as TransmissionType;
    return transLabels[key] || transmission;
  };

  const getPrice = (listing: ListingOverview) => {
    const isAuction = listing.listingType === "auction";
    return isAuction
      ? listing.auctionDetails?.currentBid ||
          listing.auctionDetails?.startingPrice ||
          0
      : listing.directPurchaseDetails?.currentPrice || 0;
  };

  const [hoveredListingId, setHoveredListingId] = useState<string | null>(null);

  if (publicListingsQuery.isLoading) {
    if (isNative) {
      return <NativeListingsSkeleton count={5} />;
    }
    return null;
  }

  if (listings.length === 0) {
    return null;
  }

  return (
    <section className="">
      {!embedMode && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.22 }}
          className="text-center mb-6"
        >
          <h2 className="text-xl md:text-2xl font-bold">{labels.vehicleGrid.title}</h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            {labels.vehicleGrid.subtitle}
          </p>
        </motion.div>
      )}

      <div
        className={
          embedMode
            ? "grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 sm:gap-3"
            : "grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4"
        }
      >
        {listings.map((listing, index) => (
          <motion.div
            key={listing.id}
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.2, delay: Math.min(index, 8) * 0.015 }}
            onClick={(e) => onCarClick(listing, e)}
            onMouseEnter={() => setHoveredListingId(listing.id)}
            onMouseLeave={() =>
              setHoveredListingId((id) => (id === listing.id ? null : id))
            }
            className="group cursor-pointer"
          >
            <div
              className={
                embedMode
                  ? "bg-card rounded-lg overflow-hidden border border-border/50 hover:border-primary/30 transition-all duration-150 hover:shadow-md hover:shadow-primary/5"
                  : "bg-card rounded-xl overflow-hidden border border-border/50 hover:border-primary/30 transition-all duration-150 hover:shadow-lg hover:shadow-primary/5"
              }
              style={
                isNative
                  ? ({
                      viewTransitionName: listingCardTransitionName(listing.id),
                    } as React.CSSProperties)
                  : undefined
              }
            >
              {/* Image container */}
              <div className="relative aspect-[4/3] overflow-hidden">
                <ListingCardThumbnail
                  src={listing.thumbnail}
                  hoverSrc={listing.hoverThumbnail}
                  alt={listing.title}
                  className="absolute inset-0 w-full h-full"
                  imgClassName="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                  loading="lazy"
                  preloadHover={hoveredListingId === listing.id}
                />
                <div className="absolute top-2 left-2">
                  <SaleTypeBadge
                    saleType={
                      listing.listingType === "auction" ? "auction" : "direct-purchase"
                    }
                    className={embedMode ? "text-[9px]" : "text-[10px]"}
                  />
                </div>
              </div>

              {/* Content */}
              <div className={embedMode ? "p-2" : "p-3"}>
                <h3
                  className={
                    embedMode
                      ? "font-semibold text-xs line-clamp-1 mb-0.5 group-hover:text-primary transition-colors"
                      : "font-semibold text-sm line-clamp-1 mb-1 group-hover:text-primary transition-colors"
                  }
                >
                  {listing.title}
                </h3>

                <div className={embedMode ? "mb-1" : "mb-2"}>
                  {listing.listingType === "direct-purchase" && listing.directPurchaseDetails ? (
                    (() => {
                      const priceInfo = formatPriceWithReduction(
                        listing.directPurchaseDetails.currentPrice || 0,
                        listing.directPurchaseDetails.originalPrice
                      );
                      return (
                        <>
                    <p
                      className={
                        embedMode
                          ? `font-bold text-sm ${priceInfo.showReduction ? "text-red-700 dark:text-red-400" : "text-black dark:text-white"}`
                          : `font-bold text-base ${priceInfo.showReduction ? "text-red-700 dark:text-red-400" : "text-black dark:text-white"}`
                      }
                    >
                      {priceInfo.price}
                    </p>
                          {priceInfo.showReduction && priceInfo.reductionText && (
                            <p className="text-xs text-green-600 mt-0.5">
                              {priceInfo.reductionText}
                            </p>
                          )}
                        </>
                      );
                    })()
                  ) : (
                    <p
                      className={
                        embedMode
                          ? "text-black dark:text-white font-bold text-sm"
                          : "text-black dark:text-white font-bold text-base"
                      }
                    >
                      {formatPrice(getPrice(listing))}
                    </p>
                  )}
                </div>

                <div
                  className={
                    embedMode
                      ? "grid grid-cols-2 gap-x-2 gap-y-1 text-[10px] text-foreground"
                      : "grid grid-cols-2 gap-x-2 gap-y-1 text-xs text-foreground"
                  }
                >
                  {listing.vehicleDetails?.mileage != null && listing.vehicleDetails.mileage > 0 ? (
                    <div className="flex items-center gap-1">
                      <Gauge className="w-3 h-3 flex-shrink-0" />
                      <span>{formatMileage(listing.vehicleDetails.mileage)}</span>
                    </div>
                  ) : null}
                  {listing.vehicleDetails?.fuelType ? (
                    <div className="flex items-center gap-1">
                      <Fuel className="w-3 h-3 flex-shrink-0" />
                      <span>{formatFuelType(listing.vehicleDetails.fuelType)}</span>
                    </div>
                  ) : null}
                  {listing.vehicleDetails?.transmission ? (
                    <div className="flex items-center gap-1">
                      <Settings className="w-3 h-3 flex-shrink-0" />
                      <span>{formatTransmission(listing.vehicleDetails.transmission)}</span>
                    </div>
                  ) : null}
                  {listing.vehicleDetails?.year ? (
                    <div className="flex items-center gap-1">
                      <Calendar className="w-3 h-3 flex-shrink-0" />
                      <span>{listing.vehicleDetails.year}</span>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  );
};

export default VehicleListingsGrid;
